<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cortexSettingsAPI from 'dashboard/api/cortexSettings';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from '../../settings/account/components/SectionLayout.vue';

const { t } = useI18n();
const isLoading = ref(false);
const isSaving = ref(false);
const summary = ref({});
const confidence = reactive({
  confidence_policy_mode: 'observe',
  semantic_decision_mode: 'observe',
  minimum_intent_confidence: 0.65,
  minimum_retrieval_confidence: 0.6,
  minimum_grounding_confidence: 0.7,
  minimum_tool_execution_confidence: 0.9,
  minimum_semantic_decision_confidence: 0.9,
  low_confidence_action: 'ask_clarification',
  ask_clarification_enabled: true,
  grounding_validation_enabled: true,
  confidence_logging_enabled: true,
  confidence_sampling_rate: 1,
});

const modeOptions = computed(() => [
  { value: 'disabled', label: t('CORTEX_CONFIDENCE.MODE.OPTIONS.disabled') },
  { value: 'observe', label: t('CORTEX_CONFIDENCE.MODE.OPTIONS.observe') },
  { value: 'enforce', label: t('CORTEX_CONFIDENCE.MODE.OPTIONS.enforce') },
]);
const actionOptions = computed(() => [
  {
    value: 'ask_clarification',
    label: t('CORTEX_CONFIDENCE.ACTION.OPTIONS.ask_clarification'),
  },
  {
    value: 'answer_with_limitation',
    label: t('CORTEX_CONFIDENCE.ACTION.OPTIONS.answer_with_limitation'),
  },
  { value: 'abstain', label: t('CORTEX_CONFIDENCE.ACTION.OPTIONS.abstain') },
  {
    value: 'handoff_when_allowed',
    label: t('CORTEX_CONFIDENCE.ACTION.OPTIONS.handoff_when_allowed'),
  },
]);
const thresholdFields = computed(() => [
  {
    key: 'minimum_intent_confidence',
    label: t('CORTEX_CONFIDENCE.THRESHOLDS.INTENT'),
  },
  {
    key: 'minimum_retrieval_confidence',
    label: t('CORTEX_CONFIDENCE.THRESHOLDS.RETRIEVAL'),
  },
  {
    key: 'minimum_grounding_confidence',
    label: t('CORTEX_CONFIDENCE.THRESHOLDS.GROUNDING'),
  },
  {
    key: 'minimum_tool_execution_confidence',
    label: t('CORTEX_CONFIDENCE.THRESHOLDS.TOOL'),
  },
  {
    key: 'minimum_semantic_decision_confidence',
    label: t('CORTEX_CONFIDENCE.THRESHOLDS.SEMANTIC_DECISION'),
  },
]);
const toggleFields = computed(() => [
  {
    key: 'ask_clarification_enabled',
    label: t('CORTEX_CONFIDENCE.TOGGLES.ask_clarification_enabled'),
  },
  {
    key: 'grounding_validation_enabled',
    label: t('CORTEX_CONFIDENCE.TOGGLES.grounding_validation_enabled'),
  },
  {
    key: 'confidence_logging_enabled',
    label: t('CORTEX_CONFIDENCE.TOGGLES.confidence_logging_enabled'),
  },
]);
const metrics = computed(() => [
  {
    key: 'assessments',
    label: t('CORTEX_CONFIDENCE.SUMMARY.assessments'),
    value: summary.value.assessments || 0,
  },
  {
    key: 'would_block',
    label: t('CORTEX_CONFIDENCE.SUMMARY.would_block'),
    value: summary.value.would_block || 0,
  },
  {
    key: 'enforced',
    label: t('CORTEX_CONFIDENCE.SUMMARY.enforced'),
    value: summary.value.enforced || 0,
  },
  {
    key: 'tools_blocked',
    label: t('CORTEX_CONFIDENCE.SUMMARY.tools_blocked'),
    value: summary.value.tools_blocked || 0,
  },
]);

const applySettings = data => {
  Object.assign(confidence, data.confidence || {});
  summary.value = data.confidence_summary || {};
};
const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const { data } = await cortexSettingsAPI.get();
    applySettings(data);
  } catch (error) {
    useAlert(t('CORTEX_CONFIDENCE.API.ERROR'));
  } finally {
    isLoading.value = false;
  }
};
const saveSettings = async () => {
  isSaving.value = true;
  try {
    const payload = {
      confidence: {
        ...confidence,
        confidence_sampling_rate: Number(confidence.confidence_sampling_rate),
        ...Object.fromEntries(
          thresholdFields.value.map(({ key }) => [key, Number(confidence[key])])
        ),
      },
    };
    const { data } = await cortexSettingsAPI.update(payload);
    applySettings(data);
    useAlert(t('CORTEX_CONFIDENCE.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CORTEX_CONFIDENCE.API.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(fetchSettings);
</script>

<template>
  <SectionLayout
    :title="t('CORTEX_CONFIDENCE.TITLE')"
    :description="t('CORTEX_CONFIDENCE.DESCRIPTION')"
    with-border
  >
    <woot-loading-state v-if="isLoading" />
    <form v-else class="grid gap-5" @submit.prevent="saveSettings">
      <div class="grid gap-2 text-sm text-n-slate-11">
        <p>{{ t('CORTEX_CONFIDENCE.HELP.DIMENSIONS') }}</p>
        <p>{{ t('CORTEX_CONFIDENCE.HELP.RISK') }}</p>
        <p>{{ t('CORTEX_CONFIDENCE.HELP.COST') }}</p>
      </div>

      <div class="grid gap-4 md:grid-cols-2">
        <WithLabel
          name="confidence-mode"
          :label="t('CORTEX_CONFIDENCE.MODE.LABEL')"
          :help-message="t('CORTEX_CONFIDENCE.MODE.HELP')"
        >
          <AdaptiveSelect
            v-model="confidence.confidence_policy_mode"
            :options="modeOptions"
            :aria-label="t('CORTEX_CONFIDENCE.MODE.LABEL')"
          />
        </WithLabel>
        <WithLabel
          name="confidence-action"
          :label="t('CORTEX_CONFIDENCE.ACTION.LABEL')"
          :help-message="t('CORTEX_CONFIDENCE.ACTION.HELP')"
        >
          <AdaptiveSelect
            v-model="confidence.low_confidence_action"
            :options="actionOptions"
            :aria-label="t('CORTEX_CONFIDENCE.ACTION.LABEL')"
          />
        </WithLabel>
        <WithLabel
          name="semantic-decision-mode"
          :label="t('CORTEX_CONFIDENCE.SEMANTIC_MODE.LABEL')"
          :help-message="t('CORTEX_CONFIDENCE.SEMANTIC_MODE.HELP')"
        >
          <AdaptiveSelect
            v-model="confidence.semantic_decision_mode"
            :options="modeOptions"
            :aria-label="t('CORTEX_CONFIDENCE.SEMANTIC_MODE.LABEL')"
          />
        </WithLabel>
        <WithLabel
          v-for="field in thresholdFields"
          :key="field.key"
          :name="field.key"
          :label="field.label"
          :help-message="t('CORTEX_CONFIDENCE.THRESHOLDS.HELP')"
        >
          <NextInput
            v-model="confidence[field.key]"
            type="number"
            min="0"
            max="1"
            step="0.01"
            class="w-full"
          />
        </WithLabel>
        <WithLabel
          name="confidence-sampling"
          :label="t('CORTEX_CONFIDENCE.SAMPLING.LABEL')"
          :help-message="t('CORTEX_CONFIDENCE.SAMPLING.HELP')"
        >
          <NextInput
            v-model="confidence.confidence_sampling_rate"
            type="number"
            min="0"
            max="1"
            step="0.01"
            class="w-full"
          />
        </WithLabel>
      </div>

      <div class="grid gap-3 md:grid-cols-2">
        <label
          v-for="field in toggleFields"
          :key="field.key"
          class="flex items-center gap-3 text-sm text-n-slate-12"
        >
          <NextCheckbox v-model="confidence[field.key]" class="shrink-0" />
          <span>{{ field.label }}</span>
        </label>
      </div>

      <div id="cortex-confidence-summary" class="grid gap-3 scroll-mt-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CORTEX_CONFIDENCE.SUMMARY.TITLE') }}
        </h3>
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div
            v-for="metric in metrics"
            :key="metric.key"
            class="flex h-full min-h-24 flex-col justify-between rounded-lg border border-n-weak p-3"
          >
            <span class="text-sm text-n-slate-11">
              {{ metric.label }}
            </span>
            <span class="text-xl font-semibold text-n-slate-12">{{
              metric.value
            }}</span>
          </div>
        </div>
      </div>

      <NextButton
        type="submit"
        blue
        icon="i-lucide-save"
        :is-loading="isSaving"
      >
        {{ t('CORTEX_CONFIDENCE.SAVE') }}
      </NextButton>
    </form>
  </SectionLayout>
</template>
